import React, { useEffect, useState } from 'react';
import { Box, Heading, Text, FormControl, FormLabel, Flex, IconButton } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from '../../../utils/reduxHooks';
import { selectMarksList, selectModelsList } from '../../../modules/catalog/selectors';
import {
  getCatalogAction,
  getModelsAction,
  setCarMarkAction,
  setCarModelAction,
  setCarTypeAction,
  setSelectedMarkAction,
} from '../../../modules/catalog/actions';
import { SearchBarWithResults } from '../../components/searchByVINInput';
import { SelectCustom } from '../../components/UI/inputs/select';
import { ButtonCustom } from '../../components/UI';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { routes } from '../../../constants/routes';

export const ModelSelection: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { type, mark } = useParams();
  const markList = useAppSelector(selectMarksList);
  const modelList = useAppSelector(selectModelsList);

  // Состояние для управления текущей выбранной маркой
  const [selectedMark, setSelectedMark] = useState<string | undefined>(mark || '');
  const [selectedModel, setSelectedModel] = useState<string | undefined>('');

  useEffect(() => {
    // Загрузка моделей для выбранного типа и марки
    if (type && mark) {
      dispatch(getModelsAction.request({ type, mark: mark }));
    }
  }, [type, mark, dispatch]);

  useEffect(() => {
    // Загрузка списка марок, если он пуст
    if (!markList) {
      // dispatch(getCatalogAction.request());
    }
  }, [markList, dispatch]);

  useEffect(() => {
    // Обновляем состояние марки при изменении URL
    if (markList && mark && mark !== selectedMark) {
      const defaultMark = markList.find(m => m.id === String(mark));
      if (defaultMark) {
        setSelectedMark(defaultMark.id);
        dispatch(setSelectedMarkAction(defaultMark));
        dispatch(setCarMarkAction(defaultMark.id));

        // Также можно подгрузить модели, если марка изменилась
        if (type) {
          dispatch(getModelsAction.request({ type, mark: defaultMark.id }));
        }
      }
    }
  }, [mark, type, markList, selectedMark, dispatch]);

  const markOptions = markList
    ? markList?.map(mark => ({
        value: mark.id,
        label: mark.name,
      }))
    : [];

  const modelOptions = modelList
    ? modelList?.map(model => ({
        value: model.id,
        label: model.name,
      }))
    : [];

  const handleMarkChange = (markId: string) => {
    const selected = markList?.find(mark => mark.id === markId);

    if (selected) {
      // Обновление состояния и маркер выбранной марки
      dispatch(setCarTypeAction(selected.brandId || ''));
      dispatch(setSelectedMarkAction(selected));
      dispatch(setCarMarkAction(selected.id));
      setSelectedMark(selected.id);

      // Очистка выбранной модели
      setSelectedModel('');

      // Загрузка списка моделей для выбранной марки
      if (type) {
        dispatch(getModelsAction.request({ type, mark: selected.id }));
      }

      // Обновление URL
      navigate(`/catalog/${type}/${selected.id}`);
    }
  };

  const handleModelChange = (modelId: string) => {
    dispatch(setCarModelAction(modelId));
    setSelectedModel(modelId);
  };

  const handleBack = () => {
    navigate(routes.MAIN);
    dispatch(setCarTypeAction(''));
  };

  const handleSubmit = () => {
    navigate(
      generatePath(routes.CATALOG_MODIFICATION, {
        type,
        mark,
        model: selectedModel,
      }),
    );
  };

  return (
    <Box p="32px">
      <Flex alignItems="center" onClick={handleBack} cursor="pointer">
        <IconButton aria-label="Back" icon={<ChevronLeftIcon boxSize="32px" />} variant="ghost" />
        <Heading size="lg">Каталог запчастей</Heading>
      </Flex>

      <Box display="flex" flexDirection="row" justifyContent="space-between" gap="32px" mt="48px">
        {/* VIN Search Section */}
        <Box p={6} borderWidth="1px" borderRadius="md" boxShadow="base" minW="476px">
          <Heading size="md" mb={4} fontWeight="bold">
            Поиск по VIN-номеру
          </Heading>
          <SearchBarWithResults
            placeholder="WAUBH54B11N111054"
            mb={4}
            backgroundColor="#fff"
            borderColor="#CBD5E0"
          />
          <Text fontSize="sm" mb={6}>
            Идентификационный номер (VIN) указан в <br />
            свидетельстве о регистрации (СТС) и паспорте <br />
            транспортного средства (ПТС).
            <br />
            <br />
            Если сомневаетесь, подойдет ли выбранная деталь к<br />
            вашему автомобилю, свяжитесь с Вашим менеджером.
            <br />
            Если Вы не знаете VIN, попробуйте подобрать модель <br />
            по параметрам. {'----->'}
          </Text>
          {/* <ButtonCustom variant="withBackground" width="100%" size="md">
            Проверить
          </ButtonCustom> */}
        </Box>

        {/* Model Selection Section */}
        <Box width="100%" p={6} borderWidth="1px" borderRadius="md" boxShadow="base">
          <Heading size="md" mb={6} fontWeight="bold">
            Выбор модели по параметрам
          </Heading>

          <FormControl mb={4}>
            <FormLabel fontWeight="bold" fontSize="sm">
              Марка
            </FormLabel>
            <SelectCustom
              placeholder="Выбрать марку"
              options={markOptions}
              value={selectedMark}
              onChange={handleMarkChange}
            />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel fontWeight="bold" fontSize="sm">
              Модель
            </FormLabel>
            <SelectCustom
              placeholder="Выбрать модель"
              options={modelOptions}
              onChange={handleModelChange}
              isDisabled={!selectedMark}
            />
          </FormControl>

          <ButtonCustom variant="withBackground" width="100%" size="md" onClick={handleSubmit}>
            Показать
          </ButtonCustom>
        </Box>
      </Box>
    </Box>
  );
};
